import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import Button from '@/shared/Button'

import CreateTestCompanyCard from '@/components/TestCompany/CreateTestCompanyCard'

import { v4 as uuidv4 } from 'uuid'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

const mapStateToProps = ({ loading }) => ({ isLoading: loading['testCompany/create'] })

const MAX_FIELDS = 10

const CreateTestCompanyDialog = ({
  dispatch,
  isLoading,
  onClose,
  onComplete,
  onFileChange,
  ...props
}) => {
  const { t } = useTranslation()
  const [fields, setFields] = useState([
    { id: uuidv4(), name: '', fileName: '', file: null, check: false },
  ])

  const handleAddField = () => {
    if (fields.length < MAX_FIELDS) {
      setFields([...fields, { id: uuidv4(), name: '', fileName: '', file: null, check: false }])
    }
  }

  const handleClose = () => {
    setFields([{ id: uuidv4(), name: '', fileName: '', file: null, check: false }])
    onClose?.()
  }

  const handleDeleteField = id => {
    if (fields.length === 1) {
      handleClose()
    }
    setFields(fields.filter(fieldObj => fieldObj.id !== id))
  }

  const validate = () => {
    const names = fields.map(item => item.name)
    const uniqueNames = new Set(names)

    if (uniqueNames.size !== names.length) {
      dispatch({
        type: 'app/notify',
        payload: { message: t('errors.company_logo_name'), severity: 'error' },
      })
    }
  }

  const handleChange = (id, field, value) => {
    if (field === 'file') {
      setFields(prevFields =>
        prevFields.map(fieldObj =>
          fieldObj.id === id ? { ...fieldObj, file: value, fileName: value.name } : fieldObj,
        ),
      )
    } else {
      setFields(
        fields.map(fieldObj => (fieldObj.id === id ? { ...fieldObj, [field]: value } : fieldObj)),
      )
    }
  }

  const handleSubmit = () => {
    const check = fields.filter(o => o.check === false)

    if (check.length > 0) {
      dispatch({
        type: 'app/notify',
        payload: { message: t('errors.company_logo_data'), severity: 'error' },
      })
      return
    }

    // check test company name only
    validate()
    // Perform the API call to upload the files and names
    fields.forEach(field => {
      if (field.file === null) {
        dispatch({
          type: 'testCompany/createNoneFile',
          payload: {
            status: 'A',
            name: field.name,
          },
          successCallback: () => {
            dispatch({
              type: 'app/notify',
              payload: { message: t('messages.user_company_created'), severity: 'success' },
            })
            onComplete?.()
            handleClose?.()
          },
          errorCallback: messages => {
            dispatch({
              type: 'app/notify',
              payload: { message: messages, severity: 'error' },
            })
            // console.log('message', message)
          },
        })
      } else {
        // Perform your API call here with field.name and field.file
        dispatch({
          type: 'testCompany/create',
          payload: {
            status: 'A',
            name: field.name,
            file: field.file,
          },
          successCallback: () => {
            dispatch({
              type: 'app/notify',
              payload: { message: t('messages.user_company_created'), severity: 'success' },
            })
            onComplete?.()
            handleClose?.()
          },
          errorCallback: err => {
            // console.log('message', err)
            dispatch({
              type: 'app/notify',
              payload: { message: err, severity: 'error' },
            })
          },
        })
      }
    })
  }

  return (
    <Dialog {...props} fullWidth={true}>
      {/* title */}
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.add_logo')}
      </DialogTitle>

      {/* first cloumn */}
      <DialogContent>
        <Grid>{t('general.test_company_image_title')}</Grid>
        <Box>
          {fields.map((obj, index) => (
            <div key={obj.id}>
              <CreateTestCompanyCard
                key={`category_${index + 1}`}
                field={obj}
                handleDeleteField={handleDeleteField}
                onChange={handleChange}
              />
            </div>
          ))}
          {fields?.length < MAX_FIELDS && (
            <Grid item>
              <IconButton size="small" onClick={handleAddField} color="secondary_80">
                <AddCircleOutlineIcon fontSize="small" color="secondary_80" />
                {t('general.add_logo')}
              </IconButton>
            </Grid>
          )}
        </Box>
      </DialogContent>

      {/* button */}
      <DialogActions sx={{ alignSelf: 'center' }}>
        {/* cancel */}
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>

        {/* apply */}
        <Button
          sx={{ minWidth: 150 }}
          loading={isLoading}
          disabled={isLoading}
          onClick={() => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
export default connect(mapStateToProps)(CreateTestCompanyDialog)
